import { ChangeEvent, useState, useEffect, useRef } from "react";
import { Button, Form } from "react-bootstrap";

import Radio from "components/shared/Form/Radio";
import Layout1 from "assets/svg/heatmap-layout/layout1.svg";
import DashboardCreateSelectSensorModal from "components/dashboard/create/modal/SelectDevices";
import HeatmapCombinedSensorList from "./HeatmapCombinedSensorList";
import HeatmapSingleSensorList from "./HeatmapSingleSensorList";
import { RemoveIcon } from "components/common";
import { showErrorAlert } from "utils/alert";
import { MAX_FILE_SIZE_MB } from "constant";

import "./styles.css";

const RowSelection = ({ children, onClick, selected = false }: any) => {
    return (
        <div
            className={`create-pcs-chart-row-selection${
                selected ? " create-pcs-chart-row-selection__selected" : ""
            }`}
            onClick={() => typeof onClick === "function" && onClick()}
            onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    typeof onClick === "function" && onClick();
                }
            }}
            tabIndex={0}
        >
            <div>
                <Radio checked={!!selected} />
            </div>
            <div>{children}</div>
        </div>
    );
};

export const HeatmapLayout = ({ selected }: any) => {
    return (
        <RowSelection selected={!!selected}>
            <div style={{ display: "flex" }}>
                {/* Set width and height to render before the image is loaded */}
                <img src={Layout1} alt="Layout 1" />
            </div>
        </RowSelection>
    );
};

const CreatePeopleHeatmapCharts = ({ onChange }: any) => {
    const fileRef = useRef<any>(null);
    const [displayType, setDisplayType] = useState(1);
    const [heatmapLayout] = useState(1);
    const [selectSensorModal, setSelectSensorModal] = useState<any>(null);
    const [singleChartList, setSingleChartList] = useState<any[]>([]);
    const sensorType = "People Count Sensor";
    const [chartName, setChartName] = useState("People Count Heatmap");
    const [combinedSensorList, setCombinedSensorList] = useState<any[] | null>(
        null
    );
    const [uploadImage, setUploadImage] = useState<File | null>(null);

    useEffect(() => {
        if (typeof onChange === "function") {
            onChange({
                displayType,
                heatmapLayout,
                singleChartList,
                chartName,
                combinedSensorList,
                uploadImage,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        displayType,
        heatmapLayout,
        singleChartList,
        chartName,
        combinedSensorList,
        uploadImage,
    ]);

    const removeFileUpload = () => {
        fileRef.current.value = "";
        setUploadImage(null);
    };

    const handleChangeUploadImage = (e: ChangeEvent<any>) => {
        const file = e.target.files?.[0];

        if (!file?.name) {
            removeFileUpload();
            return;
        }

        const fileSize = (file?.size || 0) / 1024 / 1024;
        if (fileSize > MAX_FILE_SIZE_MB) {
            showErrorAlert({
                message: `Image size should be less than ${MAX_FILE_SIZE_MB} MB.`,
            });
            removeFileUpload();
            return;
        }

        setUploadImage(file);
    };

    return (
        <div>
            <p>Heatmap chart of</p>
            <div style={{ gap: 20, display: "flex", flexDirection: "column" }}>
                <RowSelection
                    onClick={() => setDisplayType(1)}
                    selected={displayType === 1}
                >
                    <div>Combined Sensor Data</div>
                    <div className="create-pcs-chart-row-selection-body">
                        Plot data from multiple people count sensors onto a
                        single chart for a comprehensive overview.
                    </div>
                </RowSelection>

                <RowSelection
                    onClick={() => setDisplayType(2)}
                    selected={displayType === 2}
                >
                    <div>Individual Sensor Data</div>
                    <div className="create-pcs-chart-row-selection-body">
                        Create separate charts for each people count sensor,
                        allowing detailed analysis of individual sensor data.
                    </div>
                </RowSelection>
            </div>

            {displayType === 1 && (
                <>
                    <Form.Group className="mt-4">
                        <Form.Label>Chart Name</Form.Label>
                        <Form.Control
                            name="chart_name"
                            type="text"
                            defaultValue={""}
                            placeholder=""
                            value={chartName}
                            onChange={(event) =>
                                setChartName(event.target.value)
                            }
                        />
                    </Form.Group>

                    <Form.Group className="mt-4">
                        <Form.Label>Heatmap Layout</Form.Label>
                        <div style={{ display: "flex" }}>
                            <HeatmapLayout selected={heatmapLayout === 1} />
                        </div>
                    </Form.Group>

                    <Form.Group className="mt-4">
                        <Form.Label>
                            Upload image for selected layout
                        </Form.Label>
                        <div>
                            {!uploadImage && (
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        fileRef.current.click();
                                    }}
                                >
                                    Upload Layout Image
                                </Button>
                            )}
                            <Form.File
                                ref={fileRef}
                                style={{ display: "none" }}
                                accept=".jpeg,.jpg,.png"
                                onChange={handleChangeUploadImage}
                            />

                            {uploadImage && (
                                <div
                                    style={{
                                        padding: "14px 16px",
                                        gap: 14,
                                        minWidth: 179,
                                        borderRadius: 5,
                                        display: "inline-flex",
                                        justifyContent: "space-around",
                                        alignItems: "center",
                                        background: "#394662",
                                    }}
                                >
                                    <div>{uploadImage.name}</div>
                                    <div>
                                        <Button
                                            variant="link"
                                            className="p-0 text-decoration-none d-flex"
                                            onClick={() => removeFileUpload()}
                                        >
                                            <RemoveIcon />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Form.Group>

                    <div
                        className="mt-4"
                        style={{ borderTop: "1px solid #3E4B67" }}
                    ></div>

                    <Form.Group className="mt-4">
                        <Form.Label>
                            Sensors for combined heatmap chart
                        </Form.Label>
                        <HeatmapCombinedSensorList
                            onSelectSensor={(modal: any) => {
                                setSelectSensorModal(modal);
                            }}
                            onChange={(data: any) =>
                                setCombinedSensorList(data)
                            }
                            sensors={combinedSensorList}
                        />
                    </Form.Group>
                </>
            )}

            {displayType === 2 && (
                <Form.Group className="mt-4">
                    <Form.Label>
                        Sensors for individual heatmap charts
                    </Form.Label>

                    <div className="mt-4">
                        <Button
                            variant="primary"
                            onClick={() => {
                                setSelectSensorModal({
                                    show: true,
                                    multipleSelection: true,
                                    selectedSensors: (
                                        singleChartList || []
                                    ).pluck("device_id"),
                                    handler: (data: any) => {
                                        const sensors = Object.values(data);
                                        setSingleChartList(sensors);
                                    },
                                });
                            }}
                        >
                            Select Sensors
                        </Button>
                    </div>

                    <HeatmapSingleSensorList
                        onChange={(data: any) => setSingleChartList(data)}
                        sensors={singleChartList}
                    />
                </Form.Group>
            )}

            <DashboardCreateSelectSensorModal
                allowSensorFilter={false}
                multipleSelection={!!selectSensorModal?.multipleSelection}
                show={!!selectSensorModal}
                deviceType="Sensor"
                onHide={() => setSelectSensorModal(null)}
                values={selectSensorModal?.selectedSensors}
                excludeDevices={selectSensorModal?.excludeDevices || []}
                sensorTypes={[sensorType]}
                onSave={(event, data) => {
                    selectSensorModal.handler(data);
                    setSelectSensorModal(null);
                }}
            />
        </div>
    );
};

export default CreatePeopleHeatmapCharts;
